import { useEffect, useState, useCallback } from "react";
import api, { inr, formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calendar, MapPin, Users, Pencil, XCircle, ShieldAlert } from "lucide-react";

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editBooking, setEditBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings/me");
      setBookings(data);
    } catch (err) {
      console.error("Failed to load bookings:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (b) => {
    setEditBooking(b);
    setEditForm({
      check_in: b.check_in || "", check_out: b.check_out || "",
      adults: b.adults ?? b.guests ?? 1, children: b.children ?? 0, infants: b.infants ?? 0,
      guest_name: b.guest_name || "", guest_phone: b.guest_phone || "",
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        check_in: editForm.check_in, check_out: editForm.check_out,
        adults: Number(editForm.adults), children: Number(editForm.children), infants: Number(editForm.infants),
        guest_name: editForm.guest_name, guest_phone: editForm.guest_phone,
      };
      await api.put(`/bookings/${editBooking.id}`, payload);
      toast.success("Booking updated!");
      setEditBooking(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  const cancel = (b) => { setCancelTarget(b); setAccepted(false); };

  const refundEstimate = (b) => {
    if (!b || b.status !== "confirmed") return null;
    const hrs = (new Date(b.check_in + "T00:00:00").getTime() - Date.now()) / 3.6e6;
    const pct = hrs >= 48 ? 100 : 50;
    const reservation = (b.room_total || 0) + (b.gst_amount || 0);
    return { pct, amount: Math.round((reservation * pct) / 100) };
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/bookings/${cancelTarget.id}/cancel`);
      toast.success(data.refund_note || "Booking cancelled");
      setCancelTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cancel failed");
    } finally { setCancelling(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl font-display font-bold mb-2">My bookings</h1>
      <p className="text-[#717171] mb-8">Your reservations and trips</p>

      {loading ? (
        <p className="text-[#717171]">Loading...</p>
      ) : bookings.length === 0 ? (
        <div className="text-center py-16 text-[#717171]">
          You have no bookings yet. <a href="/search" className="text-[#FF385C] font-semibold">Find your next stay →</a>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-card-${b.id}`} className="bg-white border border-[#EBEBEB] rounded-2xl p-6 flex flex-col sm:flex-row gap-4 justify-between">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="font-display font-semibold text-lg">{b.hotel_name}</h3>
                  <Badge className={
                    b.status === "confirmed" ? "bg-[#008A05]/10 text-[#008A05] hover:bg-[#008A05]/15 border-0" :
                    b.status === "cancelled" ? "bg-[#C13515]/10 text-[#C13515] border-0" :
                    "bg-[#FFB400]/10 text-[#A07000] border-0"
                  }>{b.status}</Badge>
                </div>
                <p className="text-sm text-[#717171] mb-3">{b.room_name}</p>
                <div className="grid sm:grid-cols-3 gap-3 text-sm text-[#444]">
                  <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {b.check_in} → {b.check_out}</span>
                  <span className="flex items-center gap-1"><Users className="w-4 h-4" /> {b.guests} guests · {b.nights} nights</span>
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> Booking #{b.id.slice(0, 8)}</span>
                </div>
              </div>
              <div className="flex flex-col items-end justify-between gap-3">
                <div className="text-right">
                  <p className="text-xs text-[#717171]">Total</p>
                  <p className="font-display font-bold text-2xl">{inr(b.total_price)}</p>
                  {b.payment_id && <p className="text-[10px] text-[#717171] mt-1">{b.payment_id}</p>}
                </div>
                {b.status !== "cancelled" && (
                  <div className="flex gap-2">
                    <Button data-testid={`edit-booking-${b.id}`} variant="outline" className="rounded-xl h-8 text-xs" onClick={() => openEdit(b)}>
                      <Pencil className="w-3.5 h-3.5 mr-1" />Modify
                    </Button>
                    <Button data-testid={`cancel-booking-${b.id}`} variant="outline" className="rounded-xl h-8 text-xs text-[#C13515] border-[#F3C0B3]" onClick={() => cancel(b)}>
                      <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editBooking} onOpenChange={(o) => !o && setEditBooking(null)}>
        <DialogContent className="rounded-2xl" data-testid="edit-booking-dialog">
          <DialogHeader><DialogTitle>Modify booking</DialogTitle></DialogHeader>
          {editBooking && (
            <div className="space-y-3">
              <p className="text-sm text-[#717171]">{editBooking.hotel_name} · {editBooking.room_name}</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs mb-1 block">Check-in</Label><Input data-testid="edit-checkin" type="date" value={editForm.check_in} onChange={(e) => setEditForm({ ...editForm, check_in: e.target.value })} className="rounded-xl" /></div>
                <div><Label className="text-xs mb-1 block">Check-out</Label><Input data-testid="edit-checkout" type="date" value={editForm.check_out} onChange={(e) => setEditForm({ ...editForm, check_out: e.target.value })} className="rounded-xl" /></div>
                <div><Label className="text-xs mb-1 block">Adults (8+)</Label><Input data-testid="edit-adults" type="number" min={1} value={editForm.adults} onChange={(e) => setEditForm({ ...editForm, adults: e.target.value })} className="rounded-xl" /></div>
                <div><Label className="text-xs mb-1 block">Children (&lt;8, free)</Label><Input data-testid="edit-children" type="number" min={0} value={editForm.children} onChange={(e) => setEditForm({ ...editForm, children: e.target.value })} className="rounded-xl" /></div>
                <div><Label className="text-xs mb-1 block">Guest name</Label><Input value={editForm.guest_name} onChange={(e) => setEditForm({ ...editForm, guest_name: e.target.value })} className="rounded-xl" /></div>
                <div><Label className="text-xs mb-1 block">Guest phone</Label><Input value={editForm.guest_phone} onChange={(e) => setEditForm({ ...editForm, guest_phone: e.target.value })} className="rounded-xl" /></div>
              </div>
              <p className="text-xs text-[#717171]">Total is recalculated automatically based on dates & guests.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="rounded-xl" onClick={() => setEditBooking(null)}>Cancel</Button>
            <Button data-testid="save-booking-edit" disabled={saving} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white" onClick={saveEdit}>{saving ? "Saving..." : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="rounded-2xl" data-testid="cancel-policy-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#FF385C]" /> Cancellation Policy</DialogTitle>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-4">
              <p className="text-sm text-[#717171]">Please read and accept our cancellation policy before cancelling <strong className="text-[#222]">{cancelTarget.hotel_name}</strong>.</p>
              <ul className="space-y-2 text-sm text-[#444] bg-[#F7F7F9] border border-[#EBEBEB] rounded-xl p-4">
                <li>• <strong>Full refund</strong> if you cancel <strong>48+ hours</strong> before check-in.</li>
                <li>• <strong>50% refund</strong> if you cancel <strong>within 48 hours</strong> of check-in.</li>
                <li>• <strong>No refund</strong> after check-in or for no-shows.</li>
                <li>• The Ixitrip convenience fee is non-refundable.</li>
              </ul>
              {(() => {
                const est = refundEstimate(cancelTarget);
                if (cancelTarget.status !== "confirmed") return <p className="text-sm text-[#717171]" data-testid="cancel-refund-line">No payment was captured for this booking — nothing to refund.</p>;
                return (
                  <div data-testid="cancel-refund-line" className={`rounded-xl p-3 text-sm ${est.pct === 100 ? "bg-[#E6F4EA] text-[#137333]" : "bg-[#FEF3E0] text-[#8a5a12]"}`}>
                    Based on your check-in date, you'll receive a <strong>{est.pct}% refund (~{inr(est.amount)})</strong> to your original payment method within 2–5 business days.
                  </div>
                );
              })()}
              <label className="flex items-start gap-2 text-sm text-[#222] cursor-pointer">
                <input type="checkbox" data-testid="cancel-accept-checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#FF385C]" />
                <span>I have read and accept the cancellation policy above.</span>
              </label>
              <p className="text-xs text-[#717171]">A confirmation with your refund status will be emailed to {cancelTarget.guest_email || "your email"}.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="rounded-xl" onClick={() => setCancelTarget(null)}>Keep booking</Button>
            <Button data-testid="confirm-cancellation-btn" disabled={!accepted || cancelling} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white disabled:opacity-50" onClick={confirmCancel}>
              {cancelling ? "Cancelling..." : "I accept — cancel booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
